import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { AuthProvider } from './context/AuthContext'
import Layout from './components/layout/Layout'
import { ErrorBoundary } from './components/layout/ErrorBoundary'
import { persistOptions, queryClient } from './queries/queryClient'
import { pageModules, preloadPagesWhenIdle } from './pageModules'

const Home = lazy(pageModules.home)
const Tasks = lazy(pageModules.tasks)
const TaskPage = lazy(pageModules.taskPage)
const Settings = lazy(pageModules.settings)
const Design = lazy(() => import('./pages/Design'))

const Handbook = lazy(pageModules.handbook)
const HandbookDocument = lazy(pageModules.handbookDocument)
const HandbookProposals = lazy(pageModules.handbookProposals)

const FinanceOverview = lazy(pageModules.financeOverview)
const FinanceSales = lazy(pageModules.financeSales)
const FinanceEvents = lazy(pageModules.financeEvents)
const Transactions = lazy(pageModules.transactions)
const Invoices = lazy(pageModules.invoices)

const Profile = lazy(pageModules.profile)
const NavSettings = lazy(pageModules.navSettings)
const Team = lazy(pageModules.team)
const Users = lazy(pageModules.users)
const Hiring = lazy(pageModules.hiring)
const Applicant = lazy(pageModules.applicant)
const ApplicantReview = lazy(pageModules.applicantReview)

const OperationsEntry = lazy(pageModules.operationsEntry)
const OnTap = lazy(pageModules.onTap)
const Quality = lazy(pageModules.quality)
const Recipes = lazy(pageModules.recipes)
const KegRecipe = lazy(pageModules.kegRecipe)
const PrepRecipe = lazy(pageModules.prepRecipe)
const Closing = lazy(pageModules.closing)
const Shopping = lazy(pageModules.shopping)
const Schedule = lazy(pageModules.schedule)
const Availability = lazy(pageModules.availability)
const ShiftTemplates = lazy(pageModules.shiftTemplates)
const Timesheets = lazy(pageModules.timesheets)
const Vouchers = lazy(pageModules.vouchers)
const Leads = lazy(pageModules.leads)

export default function App() {
	useEffect(() => preloadPagesWhenIdle(), [])

	return (
		<BrowserRouter>
			<Toaster
				position="top-right"
				containerStyle={{
					top: `calc(4px + env(safe-area-inset-top))`,
					right: 18,
				}}
			/>
			<ErrorBoundary>
				<PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
					<AuthProvider>
						<Suspense fallback={<div className="min-h-screen bg-background" />}>
							<Routes>
								<Route
									path="/"
									element={
										<Layout>
											<Home />
										</Layout>
									}
								/>
								<Route
									path="/tasks"
									element={
										<Layout>
											<Tasks />
										</Layout>
									}
								/>
								<Route
									path="/tasks/:id"
									element={
										<Layout>
											<TaskPage />
										</Layout>
									}
								/>
								<Route
									path="/settings"
									element={
										<Layout>
											<Settings />
										</Layout>
									}
								/>

								<Route
									path="/finance"
									element={
										<Layout>
											<FinanceOverview />
										</Layout>
									}
								/>
								<Route
									path="/finance/sales"
									element={
										<Layout>
											<FinanceSales />
										</Layout>
									}
								/>
								<Route
									path="/finance/events"
									element={
										<Layout>
											<FinanceEvents />
										</Layout>
									}
								/>
								<Route path="/finance/transactions" element={<Navigate to="/finance/transactions/bank" replace />} />
								<Route
									path="/finance/transactions/bank"
									element={
										<Layout>
											<Transactions />
										</Layout>
									}
								/>
								<Route
									path="/finance/transactions/cash"
									element={
										<Layout>
											<Transactions />
										</Layout>
									}
								/>
								<Route
									path="/finance/transactions/equity"
									element={
										<Layout>
											<Transactions />
										</Layout>
									}
								/>
								<Route path="/finance/invoices/purchases" element={<Navigate to="/finance/invoices?kind=purchase" replace />} />
								<Route path="/finance/invoices/vat" element={<Navigate to="/finance/invoices?kind=vat" replace />} />
								<Route
									path="/finance/invoices/:id?"
									element={
										<Layout>
											<Invoices />
										</Layout>
									}
								/>

								<Route path="/training" element={<Navigate to="/handbook" replace />} />
								<Route path="/training/sop" element={<Navigate to="/handbook" replace />} />
								<Route path="/training/essentials" element={<Navigate to="/handbook" replace />} />
								<Route path="/training/quiz" element={<Navigate to="/handbook" replace />} />
								<Route path="/training/career" element={<Navigate to="/handbook/career-and-salary" replace />} />

								<Route
									path="/handbook"
									element={
										<Layout>
											<Handbook />
										</Layout>
									}
								/>
								<Route
									path="/handbook/proposals"
									element={
										<Layout>
											<HandbookProposals />
										</Layout>
									}
								/>
								<Route
									path="/handbook/:slug"
									element={
										<Layout>
											<HandbookDocument />
										</Layout>
									}
								/>

								<Route
									path="/profile"
									element={
										<Layout>
											<Profile />
										</Layout>
									}
								/>
								<Route
									path="/profile/navigation"
									element={
										<Layout>
											<NavSettings />
										</Layout>
									}
								/>
								<Route path="/staff" element={<Navigate to="/staff/team" replace />} />
								<Route
									path="/staff/team"
									element={
										<Layout>
											<Team />
										</Layout>
									}
								/>
								<Route
									path="/staff/users"
									element={
										<Layout>
											<Users />
										</Layout>
									}
								/>
								<Route
									path="/staff/hiring"
									element={
										<Layout>
											<Hiring />
										</Layout>
									}
								/>
								<Route
									path="/staff/hiring/:id"
									element={
										<Layout>
											<Applicant />
										</Layout>
									}
								/>
								<Route
									path="/staff/hiring/reviews/:id"
									element={
										<Layout>
											<ApplicantReview />
										</Layout>
									}
								/>
								<Route path="/staff/quiz-stats" element={<Navigate to="/staff/team" replace />} />
								<Route path="/staff/career" element={<Navigate to="/handbook/career-and-salary" replace />} />

								<Route
									path="/operations"
									element={
										<Layout>
											<OperationsEntry />
										</Layout>
									}
								/>
								<Route
									path="/operations/on-tap"
									element={
										<Layout>
											<OnTap />
										</Layout>
									}
								/>
								<Route
									path="/operations/quality"
									element={
										<Layout>
											<Quality />
										</Layout>
									}
								/>
								<Route
									path="/operations/recipes"
									element={
										<Layout>
											<Recipes />
										</Layout>
									}
								/>
								<Route path="/operations/kegs" element={<Navigate to="/operations/recipes?kind=keg" replace />} />
								<Route path="/operations/prep" element={<Navigate to="/operations/recipes?kind=prep" replace />} />
								<Route
									path="/operations/recipes/keg/:id"
									element={
										<Layout>
											<KegRecipe />
										</Layout>
									}
								/>
								<Route
									path="/operations/recipes/prep/:id"
									element={
										<Layout>
											<PrepRecipe />
										</Layout>
									}
								/>
								<Route
									path="/operations/kegs/:id"
									element={
										<Layout>
											<KegRecipe />
										</Layout>
									}
								/>
								<Route
									path="/operations/prep/:id"
									element={
										<Layout>
											<PrepRecipe />
										</Layout>
									}
								/>
								<Route
									path="/operations/shopping"
									element={
										<Layout>
											<Shopping />
										</Layout>
									}
								/>
								<Route
									path="/operations/closing"
									element={
										<Layout>
											<Closing />
										</Layout>
									}
								/>
								<Route
									path="/schedule"
									element={
										<Layout>
											<Schedule />
										</Layout>
									}
								/>
								<Route
									path="/schedule/availability"
									element={
										<Layout>
											<Availability />
										</Layout>
									}
								/>
								<Route
									path="/schedule/timesheets"
									element={
										<Layout>
											<Timesheets />
										</Layout>
									}
								/>
								<Route
									path="/schedule/templates"
									element={
										<Layout>
											<ShiftTemplates />
										</Layout>
									}
								/>
								<Route
									path="/vouchers"
									element={
										<Layout>
											<Vouchers />
										</Layout>
									}
								/>
								<Route path="/loyalty" element={<Navigate to="/vouchers" replace />} />
								{/* Trap: one route, not two. A separate '/leads/:id' entry remounts the
										    page on every open, resetting the calendar month underneath. */}
								<Route
									path="/leads/:id?"
									element={
										<Layout>
											<Leads />
										</Layout>
									}
								/>

								<Route path="/events" element={<Navigate to="/leads?tab=booked" replace />} />

								<Route path="/finance/bank" element={<Navigate to="/finance/transactions/bank" replace />} />
								<Route path="/finance/cash" element={<Navigate to="/finance/transactions/cash" replace />} />
								<Route path="/finance/equity" element={<Navigate to="/finance/transactions/equity" replace />} />
								<Route path="/finance/vat" element={<Navigate to="/finance/invoices?kind=vat" replace />} />
								<Route path="/reach/*" element={<Navigate to="/finance/events" replace />} />
								<Route path="/marketing/*" element={<Navigate to="/finance/events" replace />} />
								<Route path="/scanner" element={<Navigate to="/" replace />} />
								<Route path="/books/*" element={<Navigate to="/finance" replace />} />
								<Route path="/playbook/*" element={<Navigate to="/handbook" replace />} />

								<Route path="*" element={<Navigate to="/" replace />} />
								{import.meta.env.DEV && <Route path="/design" element={<Design />} />}
							</Routes>
						</Suspense>
					</AuthProvider>
				</PersistQueryClientProvider>
			</ErrorBoundary>
		</BrowserRouter>
	)
}
